import {
  H1,
  Box,
  Paragraph,
  TextField,
  Icon,
  theme,
  getCustomBrandingLogo,
  H3,
} from "@reside/ui";
import {EmailAdmissionEntryLinkDto} from "@reside/reside-api-admission";
import {useMutation, MutationStatus} from "react-query";
import {validator} from "@reside/forms/dist/validator";
import {Formik, Form} from "formik";
import Alert from "react-s-alert";

import {HeaderContainer, FormBoxWrapper, SubmitButton, Layout} from "./style";
import {AdmissionEntryService} from "../../services/AdmissionEntryService";
import {CustomLogo} from "../../atoms/custom-logo/CustomLogo";
import {AutoLogoutInfoBar} from "../../atoms/auto-logout";

type PageWelcomeRendererProps = Readonly<{
  status: MutationStatus;
  onSubmit: (values: EmailAdmissionEntryLinkDto) => void;
  onReset: () => void;
  logoUrl?: string;
}>;

export const PageWelcomeRenderer = ({
  status,
  onSubmit,
  onReset,
  logoUrl = "/assets/reside_logo_extended.svg",
}: PageWelcomeRendererProps) => {
  const displayLogoUrl = getCustomBrandingLogo(logoUrl);

  return (
    <Layout>
      <HeaderContainer>
        <CustomLogo alt="Company Logo" src={displayLogoUrl} />
        <H1 fontSize={[6]} lineHeight={["40px", "32px", "64px"]}>
          Welcome to the Reside Resident Experience
        </H1>
      </HeaderContainer>

      {status === "success" ? (
        <FormBoxWrapper>
          <Box textAlign="center">
            <Icon name="email" size={128} color={theme.color.mint100} />
            <H3 fontSize={[4, 4]}>Check your inbox!</H3>
            <Paragraph fontSize={[3, 3]} lineHeight={["24px"]}>
              Click the link in your email to enter your admission! It may take
              some time to receive an email. In case you didn&apos;t receive the
              email please contact your facility representative.
            </Paragraph>
            <SubmitButton
              color="success"
              style={{border: "none"}}
              onClick={onReset}
            >
              OK
            </SubmitButton>
          </Box>
        </FormBoxWrapper>
      ) : (
        <FormBoxWrapper>
          <Formik<EmailAdmissionEntryLinkDto>
            onSubmit={onSubmit}
            initialValues={{email: ""}}
            validate={values =>
              validator.validateAll(
                values,
                {
                  email: ["required", "email"],
                },
                {
                  required: "Please fill in your email.",
                  email: "Sorry, but that email is invalid.",
                },
              )
            }
          >
            {() => (
              <Form>
                <H3 fontSize={[4, 4]}>Login with your email</H3>
                <Paragraph fontSize={[3, 3]} lineHeight={["24px"]}>
                  To start admission, please enter your email address.
                  We&apos;ll send you a link for password-free login.
                </Paragraph>

                <TextField
                  disabled={status === "loading"}
                  type="email"
                  name="email"
                  label="E-mail Address"
                  placeholder="E-mail Address (e.g. example@reside.com)"
                />
                <SubmitButton
                  type="submit"
                  testId="sendLoginLinkButton"
                  color="primary"
                  size="large"
                  disabled={status === "loading"}
                >
                  {status === "loading" ? "Sending Link..." : "Send Link"}
                </SubmitButton>
              </Form>
            )}
          </Formik>
        </FormBoxWrapper>
      )}
    </Layout>
  );
};

export const PageWelcome = () => {
  const {mutate, status, reset} = useMutation(
    AdmissionEntryService.emailLoginLink,
    {
      onError: async (response: any) => {
        const error = await response.json();
        Alert.error(
          error?.status === 404
            ? "The email address you have entered doesn't match any account, please contact your Admission Representative."
            : "Could not send email at this time. Please, try again later.",
        );
      },
    },
  );

  return (
    <>
      <AutoLogoutInfoBar />
      <PageWelcomeRenderer status={status} onSubmit={mutate} onReset={reset} />
    </>
  );
};
